import { faLongArrowLeft, faPlus } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Table from '../../../components/common/table/table';
import Title from '../../../components/common/title';
import ClassifierValue from '../../../interfaces/classifierValue';
import Classifier from '../../../interfaces/classifier';
import { useService } from '../../../services/services';
import css from './classifier-values.module.css';
import {
  INTERNAL_CLASSIFIER_VALUE_CREATE,
  INTERNAL_CLASSIFIERS_INDEX,
} from '../../../router/routes';
import { route } from '../../../services/utils';
import { LinkComponent as Link } from '../../../components/form/elements/link';
import { ClassifierManager } from './libs/classifierManager';

export default function ClassifierValues() {
  const { t } = useTranslation();
  const { internalClassifierService } = useService();
  const { classifierId, groupKey } = useParams<{
    classifierId: string;
    groupKey: string;
  }>();
  const [title, setTitle] = useState('');
  const [clickRoute, setClickRoute] = useState('');
  const [viewRecordAbility, setViewRecordAbility] = useState(null);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    const subscription = internalClassifierService
      .classifier(groupKey, classifierId)
      .subscribe((classifier: Classifier) => {
        let cManager = new ClassifierManager(
          classifier,
          internalClassifierService,
          (key: string) => {
            return t(key);
          }
        );

        setClickRoute(ClassifierManager.getClickRoute(classifier));
        setViewRecordAbility({
          name: 'Manage '.concat(groupKey, ' classifiers'),
        });
        setColumns(cManager.getTableColumns());
        setTitle(classifier.name);
      });

    return () => subscription.unsubscribe();
  }, [
    classifierId,
    internalClassifierService,
    setTitle,
    setClickRoute,
    setColumns,
    t,
    groupKey,
  ]);

  const classifierValuesRequest = useCallback(
    (
      page: number,
      sort: string,
      desc: boolean,
      filter: { [key: string]: any }
    ) => {
      return internalClassifierService.values(
        groupKey,
        classifierId,
        page,
        sort,
        desc,
        filter
      );
    },
    [internalClassifierService, classifierId, groupKey]
  );

  return (
    <div className={css['index']}>
      <Title title={title} />
      <div className={css['body']}>
        <div className={css['toolbar']}>
          <Link
            label="back"
            linkTo={route(INTERNAL_CLASSIFIERS_INDEX, { groupKey: groupKey })}
            icon={faLongArrowLeft}
          />
          <Link
            label="add"
            primary={true}
            linkTo={route(INTERNAL_CLASSIFIER_VALUE_CREATE, {
              classifierId: classifierId,
              groupKey: groupKey,
            })}
            icon={faPlus}
          />
        </div>
        {viewRecordAbility && (
          <Table<ClassifierValue>
            request={classifierValuesRequest}
            keyAccessor="id"
            columns={columns}
            viewRoute={clickRoute}
            viewRecordAbility={viewRecordAbility}
          />
        )}
      </div>
    </div>
  );
}
